import { idToken } from './auth';

/**
 * API client. Every call carries the id token; the server derives the account from it, so
 * nothing here ever sends an account id.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; raw?: BodyInit; contentType?: string } = {},
): Promise<T> {
  const token = await idToken();

  const headers: Record<string, string> = { authorization: `Bearer ${token}` };
  let body: BodyInit | undefined;

  if (options.raw !== undefined) {
    body = options.raw;
    if (options.contentType) headers['content-type'] = options.contentType;
  } else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const response = await fetch(`/api${path}`, { method, headers, body });

  if (!response.ok) {
    // The server's message is written for a person to read, so surface it rather than
    // replacing it with a generic failure string.
    const payload = await response.json().catch(() => ({}) as Record<string, unknown>);
    throw new ApiError(
      response.status,
      String(payload.error ?? 'error'),
      String(payload.message ?? `Request failed with ${response.status}`),
      payload.detail,
    );
  }

  if (response.status === 204) return undefined as T;

  // A 200 carrying HTML means something between us and the API answered instead of the API
  // -- a CDN rewriting an error into the app shell, or a captive portal. Say that, rather
  // than surfacing a JSON parser error that describes the symptom and hides the cause.
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const body = (await response.text()).slice(0, 200);
    throw new ApiError(
      response.status,
      'bad_gateway_response',
      `Expected JSON from ${path} but got ${contentType || 'no content-type'}. ` +
        `Something between the browser and the API replaced the response. Body starts: ${body}`,
    );
  }
  return (await response.json()) as T;
}

export const api = {
  me: () => request<MeResponse>('GET', '/me'),
  createAccount: (body: { name: string; timeZone: string; jurisdictionId: string }) =>
    request<unknown>('POST', '/accounts', { body }),

  dismissPrompt: (prompt: string) =>
    request<unknown>('POST', '/me/dismiss', { body: { prompt } }),
  updateAccount: (body: {
    name?: string;
    timeZone?: string;
    jurisdictionId?: string;
    schoolYearStart?: string;
  }) => request<unknown>('PATCH', '/account', { body }),
  members: () => request<{ members: Member[] }>('GET', '/members'),
  updateMember: (userId: string, role: Member['role']) =>
    request<unknown>('PATCH', `/members/${encodeURIComponent(userId)}`, { body: { role } }),
  removeMember: (userId: string) =>
    request<unknown>('DELETE', `/members/${encodeURIComponent(userId)}`),
  createInvite: (body: { email: string; role: 'owner' | 'parent' | 'viewer' }) =>
    request<{ invite: Invite }>('POST', '/invites', { body }),
  invites: () => request<{ invites: Invite[] }>('GET', '/invites'),
  revokeInvite: (email: string) =>
    request<unknown>('DELETE', `/invites/${encodeURIComponent(email)}`),

  children: () => request<{ children: Child[] }>('GET', '/children'),
  createChild: (body: {
    name: string;
    gradeLevel?: string;
    birthDate?: string;
    jurisdictionId?: string;
  }) => request<{ child: Child }>('POST', '/children', { body }),
  updateChild: (childId: string, body: Partial<Omit<Child, 'childId'>>) =>
    request<{ child: Child }>('PATCH', `/children/${childId}`, { body }),
  previewChildPurge: (childId: string) =>
    request<{ preview: PurgePreview }>('GET', `/children/${childId}/purge`),
  purgeChild: (childId: string, confirm: string) =>
    request<{ purged: PurgePreview }>('POST', `/children/${childId}/purge`, { body: { confirm } }),

  events: (params: { from: string; to: string; childId?: string }) =>
    request<{ events: LoggedEvent[] }>('GET', `/events?${query(params)}`),
  createEvent: (body: EventInput) => request<{ event: LoggedEvent }>('POST', '/events', { body }),
  updateEvent: (
    date: string,
    eventId: string,
    body: { changes: Partial<EventInput>; expectedRevision: number; reason?: string },
  ) => request<{ event: LoggedEvent }>('PATCH', `/events/${date}/${eventId}`, { body }),
  verifyEvent: (date: string, eventId: string, expectedRevision: number, verified = true) =>
    request<{ event: LoggedEvent }>('POST', `/events/${date}/${eventId}/verify`, {
      body: { verified, expectedRevision },
    }),
  deleteEvent: (date: string, eventId: string, reason: string) =>
    request<unknown>('DELETE', `/events/${date}/${eventId}?reason=${encodeURIComponent(reason)}`),
  history: (eventId: string) =>
    request<{ history: AuditRow[] }>('GET', `/events/${eventId}/history`),

  years: () => request<YearsResponse>('GET', '/years'),
  summary: (params: { from: string; to: string; childId?: string }) =>
    request<Summary>('GET', `/summary?${query(params)}`),

  chat: (message: string, conversationId?: string) =>
    request<AssistantReply>('POST', '/assistant/chat', { body: { message, conversationId } }),
  voice: (audio: Blob, durationSec: number, conversationId?: string) =>
    request<AssistantReply>(
      'POST',
      `/assistant/voice?durationSec=${Math.ceil(durationSec)}` +
        (conversationId ? `&conversationId=${encodeURIComponent(conversationId)}` : ''),
      { raw: audio, contentType: audio.type || 'audio/wav' },
    ),
  closeConversation: (conversationId: string) =>
    request<unknown>('POST', `/assistant/conversations/${encodeURIComponent(conversationId)}/close`),

  assistantHistory: (limit = 20) =>
    request<{ turns: AssistantTurn[] }>('GET', `/assistant/history?limit=${limit}`),
  calendar: () => request<{ connections: CalendarConnection[] }>('GET', '/calendar'),
  connectCalendar: () => request<{ url: string }>('POST', '/calendar/connect'),
  syncCalendar: () => request<{ result: CalendarSyncResult }>('POST', '/calendar/sync'),
  disconnectCalendar: () => request<unknown>('DELETE', '/calendar'),
  usage: () => request<{ usage: UsageSummary }>('GET', '/usage'),
  adminAccounts: () => request<{ accounts: AdminAccount[] }>('GET', '/admin/accounts'),
  adminUpdateAccount: (
    accountId: string,
    body: { monthlyCentsCap?: number; status?: 'active' | 'suspended' },
  ) => request<unknown>('PATCH', `/admin/accounts/${accountId}`, { body }),
  apiKeys: () => request<{ keys: ApiKey[] }>('GET', '/apikeys'),
  createApiKey: (name: string) =>
    request<{ secret: string; key: ApiKey }>('POST', '/apikeys', { body: { name } }),
  revokeApiKey: (keyId: string) => request<unknown>('DELETE', `/apikeys/${keyId}`),
};

/** Exports are binary, so they bypass the JSON client and stream straight to a download. */
export async function downloadExport(params: {
  /** A school year's first date. Carries the statute that applied then. */
  year?: string;
  from?: string;
  to?: string;
  format: 'pdf' | 'xlsx';
  childId?: string;
}): Promise<void> {
  const token = await idToken();
  const response = await fetch(`/api/export?${query(params)}`, {
    headers: { authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}) as Record<string, unknown>);
    throw new ApiError(
      response.status,
      String(payload.error ?? 'error'),
      String(payload.message ?? 'Export failed'),
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `unschool-log-${params.year ?? params.from}.${params.format}`;
  link.click();
  URL.revokeObjectURL(url);
}

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, value);
  }
  return search.toString();
}

// --- shapes returned by the API -------------------------------------------

export interface Child {
  childId: string;
  name: string;
  birthDate: string | null;
  gradeLevel: string | null;
  /** Each child is measured against their own statute, not the family's. */
  jurisdictionId: string;
  active: boolean;
  color: string;
}

export interface PurgePreview {
  childName: string;
  eventsArchived: number;
  eventsAmended: number;
}

export interface Jurisdiction {
  id: string;
  label: string;
  citation: string;
  notes: string;
}

export interface ChildProgress {
  childId: string;
  name: string;
  jurisdiction: Jurisdiction;
  totals: { totalMinutes: number; coreMinutes: number; coreAtHomeMinutes: number };
  progress: RequirementProgress[];
}

export interface EventInput {
  date: string;
  startTime: string;
  endTime: string;
  subject: string;
  description: string;
  childIds: string[];
  location: 'home' | 'away';
}

export interface LoggedEvent extends EventInput {
  eventId: string;
  durationMinutes: number;
  source: string;
  verified: boolean;
  revision: number;
  updatedAt: string;
  updatedBy: string;
}

export interface Subject {
  id: string;
  label: string;
  core: boolean;
}

export interface RequirementProgress {
  label: string;
  requiredHours: number;
  loggedHours: number;
  remainingHours: number;
  satisfied: boolean;
}

export interface UsageSummary {
  month: string;
  spentCents: number;
  capCents: number;
  remainingCents: number;
}

export interface PaceRow {
  childId: string;
  name: string;
  color: string;
  weeklyTargetHours: number;
  loggedThisWeekHours: number;
  annualRequiredHours: number;
  loggedThisYearHours: number;
  annualRemainingHours: number;
  weeksRemaining: number;
  requiredPaceFromHereHours: number;
  /** Where an even pace would have you by now — the comparison that is actually useful. */
  expectedThisWeekHours: number;
  expectedThisYearHours: number;
}

export interface YearChild {
  childId: string;
  name: string;
  color: string;
  active: boolean;
  jurisdictionId: string;
  totals: { totalMinutes: number; coreMinutes: number; coreAtHomeMinutes: number };
  progress: RequirementProgress[];
  eventCount: number;
  unverifiedCount: number;
}

export interface SchoolYearRow {
  from: string;
  to: string;
  label: string;
  current: boolean;
  eventCount: number;
  totalHours: number;
  children: YearChild[];
}

export interface YearsResponse {
  schoolYearStart: string;
  years: SchoolYearRow[];
}

export interface Summary {
  from: string;
  to: string;
  totals: { totalMinutes: number; coreMinutes: number; coreAtHomeMinutes: number };
  eventCount: number;
  unverifiedCount: number;
  /** The figure that matters: hour requirements are owed per student. */
  byChild: ChildProgress[];
  /** Week-and-year pacing per child; empty for children with no hour requirement. */
  pace: PaceRow[];
  schoolYear: { from: string; to: string };
  familyDefaultJurisdiction: Jurisdiction;
}

export interface Member {
  userId: string;
  email: string;
  name: string;
  role: 'owner' | 'parent' | 'viewer';
  lastSeenAt: string | null;
  isYou: boolean;
}

export interface Invite {
  email: string;
  accountId: string;
  role: string;
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
}

export interface AdminAccount {
  accountId: string;
  name: string;
  status: 'active' | 'suspended';
  timeZone: string;
  createdAt: string;
  defaultJurisdiction: string;
  monthlyCentsCap: number;
  spentCents: number;
  childCount: number;
  members: Array<{
    userId: string;
    email: string;
    name: string;
    role: string;
    lastSeenAt: string | null;
  }>;
}

export interface CalendarConnection {
  userId: string;
  email: string;
  connectedAt: string;
  lastSyncAt: string | null;
  isYou: boolean;
}

export interface CalendarSyncResult {
  considered: number;
  imported: number;
  skipped: number;
  centsSpent: number;
}

export interface ApiKey {
  keyId: string;
  name: string;
  role: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface AuditRow {
  revision: number;
  changedFields: string[];
  reason: string;
  changedBy: string;
  changedVia: string;
  changedAt: string;
  previous: LoggedEvent;
}

export interface AssistantReply {
  conversationId: string;
  turnId: string | null;
  status: 'open' | 'closed';
  reply: string;
  transcript?: string;
  mutations: Array<{ action: string; eventId: string }>;
  toolCalls?: Array<{ name: string; ok: boolean; error: string | null }>;
  centsSpent: number;
}

export interface AssistantTurn {
  turnId: string;
  conversationId: string;
  status: 'open' | 'closed';
  at: string;
  via: 'text' | 'voice' | 'mcp';
  said: string;
  heard: string | null;
  reply: string;
  toolCalls: Array<{ name: string; ok: boolean; error: string | null }>;
  mutations: Array<{ action: string; ref: string }>;
  centsSpent: number;
  error: string | null;
}

export interface MeResponse {
  identity: { userId: string; email: string; name: string };
  member: boolean;
  superadmin?: boolean;
  canCreateAccount?: boolean;
  /** Who an uninvited visitor should ask. */
  contact?: string;
  reason?: 'no_invite' | 'expired' | 'already_used';
  message?: string;
  user?: { role: string; dismissedPrompts?: string[] };
  calendarConnected?: boolean;
  account?: {
    accountId: string;
    name: string;
    timeZone: string;
    jurisdictionId: string;
    schoolYearStart: string;
  };
  children?: Child[];
  usage?: UsageSummary;
  subjects?: Subject[];
  jurisdiction?: Jurisdiction;
  jurisdictions?: Jurisdiction[];
}
